import React, { useState, useRef } from 'react';
import { Upload, X, Check, Image as ImageIcon, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageUpload = ({ onImageUploaded, currentImage, imageType = "primary" }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      uploadImage(file);
    }
  };

  const uploadImage = async (file) => {
    setIsUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      // Validate file
      if (!file.type.startsWith('image/')) {
        throw new Error('Bitte wählen Sie eine Bilddatei aus');
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        throw new Error('Datei ist zu groß. Maximale Größe: 10MB');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);

      const response = await axios.post(`${API}/upload-profile-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.success) {
        setUploadedImage(response.data.data);
        onImageUploaded && onImageUploaded(response.data.data);
      } else {
        throw new Error(response.data.message || 'Upload fehlgeschlagen');
      }
    } catch (error) {
      setError(error.response?.data?.detail || error.message || 'Upload fehlgeschlagen');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      uploadImage(files[0]);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {imageType === 'primary' ? 'Hauptprofilbild' : 'Sekundäres Profilbild'}
        </label>
        
        {/* Current Image Preview */}
        {(currentImage || uploadedImage) && (
          <div className="mb-4 relative">
            <img
              src={uploadedImage?.base64_url || currentImage}
              alt="Current profile"
              className="w-32 h-32 object-cover rounded-lg border border-gray-600"
            />
            <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
              <Check size={16} />
            </div>
          </div>
        )}
        
        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-300 ${
            isUploading
              ? 'border-violet-500 bg-violet-500/10'
              : 'border-gray-600 hover:border-gray-500 hover:bg-gray-800/50'
          }`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={triggerFileInput}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {isUploading ? (
            <div className="space-y-4">
              <div className="w-12 h-12 bg-violet-500 rounded-lg flex items-center justify-center mx-auto">
                <Upload size={24} color="white" />
              </div>
              <div>
                <p className="text-white font-medium">Wird hochgeladen...</p>
                <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                  <div
                    className="bg-violet-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-400 mt-1">{uploadProgress}%</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto">
                <ImageIcon size={24} color="white" />
              </div>
              <div>
                <p className="text-white font-medium">Bild hochladen</p>
                <p className="text-sm text-gray-400">
                  Ziehen Sie ein Bild hierher oder klicken Sie zum Auswählen
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG bis 10MB
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
            <div className="flex items-center">
              <AlertCircle size={20} className="text-red-500 mr-2" />
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          </div>
        )}
        
        {/* Success Message */}
        {uploadedImage && (
          <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
            <div className="flex items-center">
              <Check size={20} className="text-green-500 mr-2" />
              <span className="text-green-300 text-sm">
                Bild erfolgreich hochgeladen!
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;